import { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import Pagination from "../Pagination";

export default function ContractServiceTable({ services = [], onAdd, setServices, isReadOnly = false, hasSubLocations = false }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // ⭐ เก็บ cache รูปไม่ให้ถูก createObjectURL ซ้ำ
  const [imageCache, setImageCache] = useState({});

  // สร้าง URL ครั้งเดียวต่อ service.id
  useEffect(() => {
    const newCache = {};

    services.forEach((s) => {
      if (!s.img) return;

      // ถ้าเป็น URL string ก็เก็บตรง ๆ
      if (typeof s.img === "string") {
        newCache[s.id] = s.img;
        return;
      }

      // ถ้ามี blob อยู่แล้ว และไม่เคยสร้าง URL
      if (s.img?.data) {
        const blob = new Blob([Uint8Array.from(s.img.data)], { type: "image/jpeg" });
        const url = URL.createObjectURL(blob);
        newCache[s.id] = url;
      }
    });

    // ก่อนเซ็ตค่าใหม่ → revoke ของเก่า
    Object.values(imageCache).forEach((url) => {
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    });

    setImageCache(newCache);

    return () => {
      // cleanup ตอน unmount
      Object.values(newCache).forEach((url) => {
        if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
      });
    };
  }, [services]);

  const totalItems = services.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = services.slice(startIndex, startIndex + itemsPerPage);

  const formatNumber = (val) =>
    Number(val || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6 border border-gray-300 text-[14px]">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-gray-700 text-lg">บริการที่เลือก</h3>
        {!isReadOnly && (
          <button
            onClick={onAdd}
            className="flex items-center gap-1 text-[#F17F25] text-sm font-semibold border-b-2 border-[#F17F25] pb-0.5 hover:text-[#c7641c] transition"
          >
            <Icon icon="mdi:plus-circle-outline" className="w-5 h-5" />
            เลือกบริการ
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-300">
        <table className="table-fixed w-full border border-gray-300 text-[14px]">
          <thead>
            <tr className="h-[42px]">
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 px-3 py-1 w-[5%]">
                ลำดับ
              </th>
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 px-3 py-1 w-[10%]">
                รูปภาพ
              </th>
              <th className={`bg-[#E13F57] text-white border border-gray-300 px-3 py-1 ${hasSubLocations ? "text-left w-[64%]" : "text-left w-[26%]"}`}>
                ชื่อบริการ
              </th>
              {!hasSubLocations && (
                <>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 px-3 py-1 w-[8%]">
                    จำนวน
                  </th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 px-3 py-1 w-[12%]">
                    ราคาต่อหน่วย
                  </th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 px-3 py-1 w-[10%]">
                    ส่วนลด
                  </th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 px-3 py-1 w-[12%]">
                    รวม
                  </th>
                </>
              )}
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 px-3 py-1 w-[8%]">
                จัดการ
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((s, idx) => {
                const rowIndex = startIndex + idx;
                const total = (s.qty || 0) * (s.price || 0) - (s.discount || 0);

                return (
                  <tr
                    key={s.id}
                    className="odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition h-[42px]"
                  >
                    <td className="text-center border border-gray-300 px-3 py-1 align-middle">
                      {rowIndex + 1}
                    </td>

                    <td className="border border-gray-300 text-center px-3 py-1 align-middle">
                      {imageCache[s.id] ? (
                        <img
                          src={imageCache[s.id]}
                          alt={s.name}
                          className="w-[46px] h-[46px] object-cover rounded-md mx-auto border border-gray-200"
                        />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-3 py-1 border border-gray-300 align-middle">{s.name}</td>
                    {!hasSubLocations && (
                      <>
                        <td className="text-center px-3 py-1 border border-gray-300 align-middle">
                          {s.qty}
                        </td>
                        <td className="text-right px-3 py-1 border border-gray-300 align-middle">
                          {formatNumber(s.price)}
                        </td>
                        <td className="text-right px-3 py-1 border border-gray-300 align-middle">
                          {formatNumber(s.discount)}
                        </td>
                        <td className="text-right px-3 py-1 border border-gray-300 font-semibold text-gray-700 align-middle">
                          {formatNumber(total)}
                        </td>
                      </>
                    )}
                    <td className="text-center px-3 py-1 border border-gray-300 align-middle">
                      {!isReadOnly && (
                        <button
                          onClick={() =>
                            setServices((prev) => prev.filter((_, i) => i !== rowIndex))
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                        <Icon icon="mdi:trash-can-outline" className="w-5 h-5" />
                      </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="h-[42px]">
                <td
                  colSpan={hasSubLocations ? 4 : 8}
                  className="text-center py-4 text-gray-400 border border-gray-300"
                >
                  ยังไม่มีรายการที่เลือก
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
